import React, { useState, useEffect } from "react";
import axios from "axios";
import { UserPlus, Edit2, UserX, UserCheck, X, Save, Loader2, Shield, Eye, EyeOff, KeyRound } from "lucide-react";

import adminApi, { ADMIN_API } from "@/pages/admin/adminApi";

const ROLES = [
  { value: "admin",     label: "Administrador",  desc: "Acceso total al panel" },
  { value: "tesoreria", label: "Tesorería",       desc: "Consulta y actualiza estados de pagos" },
  { value: "consulta",  label: "Solo Consulta",   desc: "Solo puede ver pagos y dashboard" },
];

const ROL_BADGE = {
  admin:     "bg-purple-100 text-purple-700 border-purple-200",
  tesoreria: "bg-blue-100 text-blue-700 border-blue-200",
  consulta:  "bg-slate-100 text-slate-600 border-slate-200",
};

// Modal crear/editar usuario
const ModalUsuario = ({ usuario, onClose, onGuardar }) => {
  const esNuevo = !usuario;
  const [form, setForm] = useState({
    username: usuario?.username || "",
    nombre_completo: usuario?.nombre_completo || "",
    email: usuario?.email || "",
    rol: usuario?.rol || "consulta",
    password: "",
    activo: usuario?.activo ?? 1,
  });
  const [showPass, setShowPass] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const handleGuardar = async () => {
    if (esNuevo && (!form.username || !form.password || !form.nombre_completo)) {
      setError("Completá todos los campos obligatorios"); return;
    }
    setGuardando(true);
    setError("");
    try {
      if (esNuevo) {
        await adminApi.post(`${ADMIN_API}/usuarios`, form);
      } else {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await adminApi.put(`${ADMIN_API}/usuarios/${usuario.id}`, payload);
      }
      onGuardar();
    } catch (e) {
      setError(e.response?.data?.detail || "Error al guardar");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="font-bold text-slate-800">{esNuevo ? "Nuevo usuario" : `Editar: ${usuario.username}`}</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Username (solo en nuevo) */}
          {esNuevo && (
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Usuario *</label>
              <input
                type="text"
                value={form.username}
                onChange={e => setForm({ ...form, username: e.target.value.toLowerCase().trim() })}
                placeholder="ej: jperez"
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
          )}

          {/* Nombre completo */}
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Nombre completo *</label>
            <input
              type="text"
              value={form.nombre_completo}
              onChange={e => setForm({ ...form, nombre_completo: e.target.value })}
              placeholder="Juan Pérez"
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Email */}
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              placeholder="jperez@pocito.gob.ar"
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Rol */}
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Rol *</label>
            <div className="space-y-2">
              {ROLES.map(r => (
                <label key={r.value} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${form.rol === r.value ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                  <input
                    type="radio"
                    value={r.value}
                    checked={form.rol === r.value}
                    onChange={() => setForm({ ...form, rol: r.value })}
                    className="accent-blue-600"
                  />
                  <div>
                    <p className="text-sm font-semibold text-slate-700">{r.label}</p>
                    <p className="text-xs text-slate-400">{r.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Contraseña */}
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">
              {esNuevo ? "Contraseña *" : "Nueva contraseña (dejar vacío para no cambiar)"}
            </label>
            <div className="relative">
              <input
                type={showPass ? "text" : "password"}
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 pr-10 text-sm focus:outline-none focus:border-blue-500"
              />
              <button type="button" onClick={() => setShowPass(!showPass)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Activo (solo edición) */}
          {!esNuevo && (
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.activo}
                onChange={e => setForm({ ...form, activo: e.target.checked ? 1 : 0 })}
                className="accent-blue-600 w-4 h-4"
              />
              <span className="text-sm text-slate-700">Usuario activo</span>
            </label>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-sm text-red-600">{error}</div>
          )}

          <button
            onClick={handleGuardar}
            disabled={guardando}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {guardando
              ? <><Loader2 className="w-4 h-4 animate-spin" />Guardando...</>
              : <><Save className="w-4 h-4" />Guardar</>
            }
          </button>
        </div>
      </div>
    </div>
  );
};


// Modal para que el usuario cambie su propia clave
const ModalCambioClave = ({ usuario, onClose }) => {
  const [form, setForm] = useState({ password_actual: "", password_nuevo: "", password_confirm: "" });
  const [showPass, setShowPass] = useState({ actual: false, nuevo: false, confirm: false });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [exito, setExito] = useState(false);

  const handleGuardar = async () => {
    if (!form.password_actual || !form.password_nuevo || !form.password_confirm) {
      setError("Completá todos los campos"); return;
    }
    if (form.password_nuevo.length < 6) {
      setError("La nueva contraseña debe tener al menos 6 caracteres"); return;
    }
    if (form.password_nuevo !== form.password_confirm) {
      setError("Las contraseñas nuevas no coinciden"); return;
    }
    setGuardando(true);
    setError("");
    try {
      await adminApi.post(`${ADMIN_API}/usuarios/cambiar-clave`, {
        password_actual: form.password_actual,
        password_nuevo: form.password_nuevo,
      });
      setExito(true);
      setTimeout(onClose, 2000);
    } catch (e) {
      setError(e.response?.data?.detail || "Error al cambiar la contraseña");
    } finally {
      setGuardando(false);
    }
  };

  const TogglePass = ({ campo }) => (
    <button type="button"
      onClick={() => setShowPass(s => ({ ...s, [campo]: !s[campo] }))}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
      {showPass[campo] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="font-bold text-slate-800">Cambiar contraseña</h2>
            <p className="text-xs text-slate-400 mt-0.5">{usuario.username}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {exito ? (
            <div className="flex flex-col items-center gap-3 py-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Save className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-green-700 font-semibold text-center">Contraseña actualizada correctamente</p>
            </div>
          ) : (
            <>
              {[
                { campo: "actual",  label: "Contraseña actual",         key: "password_actual" },
                { campo: "nuevo",   label: "Nueva contraseña",          key: "password_nuevo" },
                { campo: "confirm", label: "Confirmar nueva contraseña", key: "password_confirm" },
              ].map(({ campo, label, key }) => (
                <div key={key}>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">{label}</label>
                  <div className="relative">
                    <input
                      type={showPass[campo] ? "text" : "password"}
                      value={form[key]}
                      onChange={e => setForm({ ...form, [key]: e.target.value })}
                      placeholder="••••••••"
                      className="w-full border border-slate-200 rounded-xl px-3 py-2.5 pr-10 text-sm focus:outline-none focus:border-blue-500"
                    />
                    <TogglePass campo={campo} />
                  </div>
                </div>
              ))}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-sm text-red-600">{error}</div>
              )}

              <button
                onClick={handleGuardar}
                disabled={guardando}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                {guardando
                  ? <><Loader2 className="w-4 h-4 animate-spin" />Guardando...</>
                  : <><Save className="w-4 h-4" />Actualizar contraseña</>
                }
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────
const AdminUsuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | "nuevo" | usuario
  const [modalClave, setModalClave] = useState(false);

  // Usuario actualmente logueado (para el modal de cambio de clave propio)
  const usuarioActual = (() => {
    try { return JSON.parse(localStorage.getItem("admin_user") || "{}"); }
    catch { return {}; }
  })();

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get(`${ADMIN_API}/usuarios`);
      setUsuarios(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const toggleActivo = async (u) => {
    try {
      await adminApi.put(`${ADMIN_API}/usuarios/${u.id}`, { activo: !u.activo });
      cargar();
    } catch (e) {
      alert(e.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Usuarios</h1>
          <p className="text-slate-500 text-sm mt-1">Gestión de accesos al panel administrativo</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setModalClave(true)}
            className="flex items-center gap-2 bg-slate-600 hover:bg-slate-700 text-white font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-sm"
          >
            <KeyRound className="w-4 h-4" />
            Mi contraseña
          </button>
          <button
            onClick={() => setModal("nuevo")}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-sm"
          >
            <UserPlus className="w-4 h-4" />
            Nuevo usuario
          </button>
        </div>
      </div>

      {/* Roles info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {ROLES.map(r => (
          <div key={r.value} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-slate-400" />
              <span className={`px-2 py-0.5 rounded-lg text-xs font-bold border ${ROL_BADGE[r.value]}`}>{r.label}</span>
            </div>
            <p className="text-xs text-slate-500">{r.desc}</p>
            <p className="text-lg font-bold text-slate-700 mt-2">
              {usuarios.filter(u => u.rol === r.value && u.activo).length}
              <span className="text-xs font-normal text-slate-400 ml-1">activos</span>
            </p>
          </div>
        ))}
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                {["Usuario", "Nombre", "Email", "Rol", "Último acceso", "Estado", "Acciones"].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {usuarios.map((u) => (
                <tr key={u.id} className={`hover:bg-slate-50 transition-colors ${!u.activo ? "opacity-50" : ""}`}>
                  <td className="px-4 py-3 text-sm font-mono font-bold text-slate-700">{u.username}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{u.nombre_completo}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{u.email || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-lg text-xs font-bold border ${ROL_BADGE[u.rol] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
                      {ROLES.find(r => r.value === u.rol)?.label || u.rol}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400">
                    {u.ultimo_acceso ? u.ultimo_acceso.substring(0, 16).replace("T", " ") : "Nunca"}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-lg text-xs font-bold ${u.activo ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                      {u.activo ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button
                        onClick={() => setModal(u)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => toggleActivo(u)}
                        className={`p-1.5 rounded-lg transition-colors ${u.activo ? "text-red-400 hover:bg-red-50" : "text-green-500 hover:bg-green-50"}`}
                        title={u.activo ? "Desactivar" : "Activar"}
                      >
                        {u.activo ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal crear/editar usuario */}
      {modal && (
        <ModalUsuario
          usuario={modal === "nuevo" ? null : modal}
          onClose={() => setModal(null)}
          onGuardar={() => { setModal(null); cargar(); }}
        />
      )}

      {/* Modal cambio de clave propio */}
      {modalClave && (
        <ModalCambioClave
          usuario={usuarioActual}
          onClose={() => setModalClave(false)}
        />
      )}
    </div>
  );
};

export default AdminUsuarios;
